import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Cookie, ShieldCheck } from 'lucide-react';

const STORAGE_KEY = 'daniele_cookie_consent_v1';

const defaultPrefs = {
  necessary: true,
  statistics: false,
  marketing: false,
  timestamp: null,
};

const CookieBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) setVisible(true);
      else setPrefs(JSON.parse(stored));
    } catch (e) {
      setVisible(true);
    }
  }, []);

  const persist = (next) => {
    const data = { ...next, timestamp: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setPrefs(data);
    setVisible(false);
    setShowSettings(false);
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: data }));
  };

  const acceptAll = () => persist({ necessary: true, statistics: true, marketing: true });
  const rejectAll = () => persist({ necessary: true, statistics: false, marketing: false });
  const saveCustom = () => persist(prefs);

  const reopen = () => {
    setVisible(true);
    setShowSettings(true);
  };

  useEffect(() => {
    window.openCookiePreferences = reopen;
    return () => { delete window.openCookiePreferences; };
  });

  if (!visible) {
    return (
      <button
        type="button"
        className="cookie-floating-btn"
        onClick={reopen}
        aria-label={t('cookie.manageBtn')}
        data-testid="cookie-reopen-btn"
      >
        <Cookie size={20} />
      </button>
    );
  }

  return (
    <div className="cookie-overlay" data-testid="cookie-banner">
      <div className={`cookie-banner ${showSettings ? 'expanded' : ''}`}>
        {!showSettings ? (
          <>
            <div className="cookie-header">
              <Cookie size={24} className="cookie-icon" />
              <h3>{t('cookie.title')}</h3>
            </div>
            <p className="cookie-text">
              {t('cookie.text')}{' '}
              <Link to="/cookie-policy" className="cookie-link">{t('cookie.linkCookie')}</Link>{' '}{t('cookie.and')}{' '}
              <Link to="/privacy-policy" className="cookie-link">{t('cookie.linkPrivacy')}</Link>.
            </p>
            <div className="cookie-actions">
              <button type="button" className="cookie-btn cookie-btn-ghost" onClick={rejectAll} data-testid="cookie-reject-btn">
                {t('cookie.rejectAll')}
              </button>
              <button type="button" className="cookie-btn cookie-btn-ghost" onClick={() => setShowSettings(true)} data-testid="cookie-customize-btn">
                {t('cookie.customize')}
              </button>
              <button type="button" className="cookie-btn cookie-btn-primary" onClick={acceptAll} data-testid="cookie-accept-btn">
                {t('cookie.acceptAll')}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="cookie-header">
              <ShieldCheck size={24} className="cookie-icon" />
              <h3>{t('cookie.settingsTitle')}</h3>
              <button type="button" className="cookie-close" onClick={() => setShowSettings(false)} aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <div className="cookie-category">
              <div className="cookie-category-head">
                <div>
                  <h4>{t('cookie.necessaryTitle')}</h4>
                  <p>{t('cookie.necessaryDesc')}</p>
                </div>
                <span className="cookie-toggle disabled" aria-label={t('cookie.alwaysActive')}>
                  <span className="cookie-toggle-knob on" />
                  <em>{t('cookie.alwaysActive')}</em>
                </span>
              </div>
            </div>

            <div className="cookie-category">
              <div className="cookie-category-head">
                <div>
                  <h4>{t('cookie.statisticsTitle')}</h4>
                  <p>{t('cookie.statisticsDesc')}</p>
                </div>
                <button
                  type="button"
                  className={`cookie-toggle ${prefs.statistics ? 'active' : ''}`}
                  onClick={() => setPrefs((p) => ({ ...p, statistics: !p.statistics }))}
                  aria-pressed={prefs.statistics}
                  data-testid="cookie-toggle-statistics"
                >
                  <span className={`cookie-toggle-knob ${prefs.statistics ? 'on' : ''}`} />
                </button>
              </div>
            </div>

            <div className="cookie-category">
              <div className="cookie-category-head">
                <div>
                  <h4>{t('cookie.marketingTitle')}</h4>
                  <p>{t('cookie.marketingDesc')}</p>
                </div>
                <button
                  type="button"
                  className={`cookie-toggle ${prefs.marketing ? 'active' : ''}`}
                  onClick={() => setPrefs((p) => ({ ...p, marketing: !p.marketing }))}
                  aria-pressed={prefs.marketing}
                  data-testid="cookie-toggle-marketing"
                >
                  <span className={`cookie-toggle-knob ${prefs.marketing ? 'on' : ''}`} />
                </button>
              </div>
            </div>

            <div className="cookie-actions">
              <button type="button" className="cookie-btn cookie-btn-ghost" onClick={rejectAll} data-testid="cookie-reject-all-pref">
                {t('cookie.rejectAll')}
              </button>
              <button type="button" className="cookie-btn cookie-btn-primary" onClick={saveCustom} data-testid="cookie-save-pref">
                {t('cookie.savePref')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
